@if (isDataLoading) {
  <app-loading-spinner [message]="'Loading packaging architecture...'"></app-loading-spinner>
} @else {
  <div class="container-fluid">
    <div class="row justify-content-center">
      <!-- Main Header Card -->
      <div class="card p-3 border bg-white animate-fade-in">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div class="d-flex align-items-center">
            <button class="btn btn-icon btn-soft-secondary me-3" (click)="onCancel()" title="Go Back">
              <i class="bi bi-arrow-left"></i>
            </button>
            <div>
              <h1 class="card-title mb-0" style="font-size: 1.1rem">{{ product.name }}</h1>
              <span class="sub-text" style="color: #475569">
                {{ packaging.packing_size }} {{ packaging.unit.short_name }}
                @if (packaging.packaging_type) {
                  {{ packaging.packaging_type.name }}
                }
                Configuration
              </span>
            </div>
          </div>

          <div class="d-flex gap-2">
            <button type="button" class="btn btn-soft-secondary px-3" (click)="onCancel()">
              <span>Cancel</span>
            </button>
            <button
              type="button"
              class="btn btn-primary px-4"
              (click)="onSave()"
              [disabled]="packagingForm.invalid || isSaving || (!packagingForm.dirty && !selectedImageFile)">
              @if (isSaving) {
                <app-loading-spinner [inline]="true" class="me-2"></app-loading-spinner>
                <span>Saving...</span>
              } @else {
                <i class="bi bi-check2-circle"></i>
                <span>Save Changes</span>
              }
            </button>
          </div>
        </div>
      </div>

      @if (errorMessage) {
        <div class="alert alert-danger border-0 mb-3 d-flex align-items-center gap-3">
          <i class="bi bi-exclamation-octagon-fill fs-4"></i>
          <div class="small fw-semibold">{{ errorMessage }}</div>
          <button class="btn-close ms-auto" (click)="errorMessage = null"></button>
        </div>
      }

      <form [formGroup]="packagingForm" class="row g-3 mt-0">
        <!-- Specifications Card -->
        <div class="col-lg-7 px-2">
          <div class="card p-4 border shadow-none h-100">
            <h6 class="fw-bold mb-4 text-dark d-flex align-items-center">
              <i class="bi bi-sliders2 me-2 text-primary"></i>
              Specifications & Pricing
            </h6>

            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">Packing Size</label>
                <div class="input-group">
                  <input
                    type="number"
                    formControlName="packing_size"
                    class="form-control"
                    [class.is-invalid]="isInvalid('packing_size')" />
                  <ng-select
                    [items]="units"
                    formControlName="unit"
                    bindValue="id"
                    bindLabel="short_name"
                    [clearable]="false"
                    [searchable]="false"
                    style="max-width: 100px" />
                </div>
              </div>

              <div class="col-md-6">
                <label class="form-label">Packaging Type</label>
                <ng-select
                  placeholder="Standard"
                  [items]="packagingTypes"
                  formControlName="packaging_type"
                  bindValue="id"
                  bindLabel="name"
                  [clearable]="true"
                  [searchable]="false" />
              </div>

              <div class="col-md-6">
                <label class="form-label">Items per Box (Nos)</label>
                <input type="number" formControlName="nos" class="form-control" [class.is-invalid]="isInvalid('nos')" />
              </div>

              <div class="col-md-6">
                <label class="form-label">GST Rate (%)</label>
                <div class="input-group">
                  <input
                    type="number"
                    formControlName="gst_rate"
                    class="form-control"
                    [class.is-invalid]="isInvalid('gst_rate')" />
                  <span class="input-group-text bg-light text-muted fw-bold">%</span>
                </div>
              </div>

              <div class="col-md-6">
                <label class="form-label">Dealer Rate (Excl. GST)</label>
                <div class="input-group">
                  <span class="input-group-text bg-light text-muted fw-bold">₹</span>
                  <input
                    type="number"
                    formControlName="dealer_rate"
                    class="form-control"
                    [class.is-invalid]="isInvalid('dealer_rate')" />
                </div>
              </div>

              <div class="col-md-6">
                <label class="form-label">Dealer Rate (Incl. GST)</label>
                <div class="input-group">
                  <span class="input-group-text bg-light text-muted fw-bold">₹</span>
                  <input type="text" formControlName="dealer_rate_incl_gst" class="form-control bg-light" />
                </div>
              </div>

              <div class="col-md-6">
                <label class="form-label">MRP</label>
                <div class="input-group">
                  <span class="input-group-text bg-light text-muted fw-bold">₹</span>
                  <input
                    type="number"
                    formControlName="mrp"
                    class="form-control"
                    [class.is-invalid]="isInvalid('mrp')" />
                </div>
              </div>

              <!-- Receipt/Image Area -->
              <div class="col-12 mt-4">
                <div class="image-upload-zone p-4 border-dashed bg-light text-center cursor-pointer position-relative">
                  <input
                    type="file"
                    class="opacity-0 position-absolute w-100 h-100 top-0 start-0 cursor-pointer"
                    (change)="onImageSelected($event)"
                    accept="image/*" />

                  @if (imagePreview || packaging.image_url) {
                    <div class="d-flex align-items-center justify-content-center gap-4">
                      <img
                        [src]="imagePreview || packaging.image_url"
                        class="border"
                        style="width: 80px; height: 80px; object-fit: cover" />
                      <div class="text-start">
                        <p class="mb-1 small fw-bold text-dark">Packaging Photo</p>
                        <p class="mb-0 text-muted extra-small">Click to change or drag new file</p>
                      </div>
                      @if (imagePreview) {
                        <button
                          type="button"
                          class="btn btn-icon btn-soft-danger ms-auto"
                          (click)="onRemoveImage(); $event.stopPropagation()">
                          <i class="bi bi-trash"></i>
                        </button>
                      }
                    </div>
                  } @else {
                    <div class="py-2">
                      <i class="bi bi-cloud-arrow-up fs-2 text-primary opacity-50 mb-2 d-block"></i>
                      <span class="small fw-bold text-dark d-block">Upload Product Packaging Image</span>
                      <span class="extra-small text-muted">Supports JPG, PNG (Max 2MB)</span>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- BOM Section -->
        <div class="col-lg-5 px-2">
          <div class="card p-4 border shadow-none h-100">
            <div class="d-flex justify-content-between align-items-center mb-4">
              <h6 class="fw-bold mb-0 text-dark d-flex align-items-center">
                <i class="bi bi-list-check me-2 text-success"></i>
                Bill of Materials
              </h6>
              <button type="button" class="btn btn-soft-primary btn-sm px-3" (click)="addMaterial()">
                <i class="bi bi-plus"></i>
                Add
              </button>
            </div>

            <div class="materials-list" formArrayName="materials">
              @for (mat of materials.controls; track $index) {
                <div class="material-row p-3 border bg-light mb-2 animate-fade-in" [formGroupName]="$index">
                  <div class="row g-2 align-items-end">
                    <div class="col-12">
                      <label class="extra-small fw-bold text-muted mb-1">Raw Material</label>
                      <ng-select
                        placeholder="Select Material"
                        [items]="rawMaterials"
                        formControlName="raw_material"
                        bindValue="id"
                        bindLabel="name"
                        [clearable]="false"
                        [searchable]="true">
                        <ng-template ng-label-tmp let-item="item">
                          <span class="text-muted font-monospace me-1">{{ item.category_short_code }}</span>
                          &ndash; {{ item.name }}
                        </ng-template>
                        <ng-template ng-option-tmp let-item="item">
                          <span class="text-muted font-monospace me-1">{{ item.category_short_code }}</span>
                          &ndash; {{ item.name }}
                        </ng-template>
                      </ng-select>
                    </div>
                    <div class="col-7">
                      <label class="extra-small fw-bold text-muted mb-1">Quantity</label>
                      <input
                        type="number"
                        formControlName="quantity_per_unit"
                        class="form-control shadow-xs"
                        placeholder="0.00" />
                    </div>
                    <div class="col-3">
                      <label class="extra-small fw-bold text-muted mb-1">Unit</label>
                      <input
                        type="text"
                        formControlName="unit"
                        class="form-control shadow-xs bg-white text-dark fw-bold border-0 px-2"
                        readonly />
                    </div>
                    <div class="col-2">
                      <button
                        type="button"
                        class="btn btn-soft-danger w-100 px-0"
                        (click)="removeMaterial($index)"
                        [disabled]="materials.length === 1">
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              }

              @if (materials.length === 0) {
                <div class="text-center py-5 opacity-50">
                  <i class="bi bi-clipboard-x fs-1 d-block mb-2"></i>
                  <p class="small fw-bold mb-0">No BOM Configured</p>
                </div>
              }
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
}
